import React, { useState, useEffect } from "react";
import {
  Download,
  FileText,
  Filter,
  Search,
  Loader,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";

import FinancialReportsPanel from "./FinancialReportsPanel";
import { adminReportService } from "@/services/api/admin.report.service";

export default function FinancialReportsPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    type: "ALL_USERS_FINANCIAL",
    status: "",
    startDate: "",
    endDate: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });

  // Load reports
  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      try {
        const params = {
          page: pagination.page,
          limit: pagination.limit,
          search,
          ...filters,
        };

        const result = await adminReportService.getAllReports(params);

        if (result.success) {
          setReports(result.data.reports || []);
          setPagination(result.data.pagination || {});
        } else {
          setError("Failed to load reports");
        }
      } catch (err) {
        setError(err.message || "An error occurred while loading reports");
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [pagination.page, search, filters]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "COMPLETED":
        return "bg-green-100 text-green-800";
      case "PROCESSING":
        return "bg-blue-100 text-blue-800";
      case "QUEUED":
        return "bg-yellow-100 text-yellow-800";
      case "FAILED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Apply filters
  const applyFilters = (e) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
    // The useEffect will handle the data reload
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      type: "ALL_USERS_FINANCIAL",
      status: "",
      startDate: "",
      endDate: "",
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">
            Financial Reports
          </h2>
          <p className="text-sm text-gray-500">
            View and generate financial reports for users
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
          <div className="relative w-full md:w-64">
            <input
              type="text"
              placeholder="Search reports..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="w-full py-2 pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
            <Search className="absolute top-2.5 left-3 h-5 w-5 text-gray-400" />
          </div>

          <div className="flex gap-1">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="bg-white border border-gray-300 rounded-lg px-3 py-2 flex items-center gap-1 hover:bg-gray-50"
            >
              <Filter className="h-5 w-5 text-gray-500" />
              <span className="hidden sm:inline">Filters</span>
            </button>

            <FinancialReportsPanel />
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <form
            onSubmit={applyFilters}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Report Type
              </label>
              <select
                name="type"
                value={filters.type}
                onChange={(e) =>
                  setFilters({ ...filters, type: e.target.value })
                }
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="ALL_USERS_FINANCIAL">All Users Financial</option>
                <option value="USER_FINANCIAL">User Financial</option>
                <option value="ALL_USERS_TRANSACTIONS">All Users Transactions</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value })
                }
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="COMPLETED">Completed</option>
                <option value="PROCESSING">Processing</option>
                <option value="QUEUED">Queued</option>
                <option value="FAILED">Failed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={(e) =>
                  setFilters({ ...filters, startDate: e.target.value })
                }
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={(e) =>
                  setFilters({ ...filters, endDate: e.target.value })
                }
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="md:col-span-2 lg:col-span-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={resetFilters}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Reset
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Apply Filters
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Reports Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
            <p className="mt-4 text-gray-600">Loading reports...</p>
          </div>
        </div>
      ) : error ? (
        <div className="p-6 text-red-600 flex flex-col items-center">
          <AlertTriangle className="h-12 w-12 mb-4" />
          <h3 className="text-lg font-medium mb-2">Error Loading Reports</h3>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-red-100 hover:bg-red-200 text-red-700 font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Report ID
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date Range
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Format
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reports.length > 0 ? (
                reports.map((report) => (
                  <tr key={report.reportId} className="hover:bg-gray-50">
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {report.reportId}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {report.type === "USER_FINANCIAL"
                          ? "User Financial"
                          : report.type === "ALL_USERS_FINANCIAL"
                            ? "All Users Financial"
                            : report.type === "USER_TRANSACTIONS"
                              ? "User Transactions"
                              : report.type === "ALL_USERS_TRANSACTIONS"
                                ? "All Users Transactions"
                                : report.type}
                      </div>
                      {report.type === "USER_FINANCIAL" && (
                        <div className="text-xs text-gray-500">
                          User: {report.userData?.fullName || report.userId}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(report.dateRange?.startDate).split(",")[0]}{" "}
                        -{formatDate(report.dateRange?.endDate).split(",")[0]}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {report.format}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(
                          report.status
                        )}`}
                      >
                        {report.status}
                      </span>
                      {report.progress > 0 && report.progress < 100 && (
                        <div className="mt-1 h-1 w-16 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500"
                            style={{ width: `${report.progress}%` }}
                          ></div>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(report.createdAt)}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      {report.status === "COMPLETED" ? (
                        <button
                          onClick={() => {
                            if (report.type === "USER_FINANCIAL") {
                              adminReportService.downloadReport(
                                report.reportId
                              );
                            } else if (report.type === "USER_TRANSACTIONS" || report.type === "ALL_USERS_TRANSACTIONS") {
                              adminReportService.downloadTransactionReport(
                                report.reportId
                              );
                            } else {
                              adminReportService.downloadFinancialReport(
                                report.reportId
                              );
                            }
                          }}
                          className="bg-blue-50 hover:bg-blue-100 text-blue-600 font-medium py-1 px-3 rounded-lg text-sm flex items-center transition-colors"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </button>
                      ) : report.status === "FAILED" ? (
                        <div className="text-sm text-red-600">
                          {report.error?.message || "Failed"}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">
                          {report.status === "PROCESSING"
                            ? "Processing..."
                            : "Queued"}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    className="px-3 py-8 text-center text-gray-500"
                  >
                    <FileText className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                    <p>No reports found matching your criteria.</p>
                    <button
                      onClick={() => resetFilters()}
                      className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Clear filters
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="mt-4 flex flex-col sm:flex-row justify-between items-center border-t pt-4">
        <p className="text-sm text-gray-600 mb-2 sm:mb-0">
          Showing{" "}
          {pagination.total === 0
            ? 0
            : (pagination.page - 1) * pagination.limit + 1}{" "}
          to {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
          {pagination.total} results
        </p>
        <div className="flex gap-2">
          <button
            disabled={pagination.page === 1}
            onClick={() => handlePageChange(pagination.page - 1)}
            className={`px-3 py-1 border rounded-md ${pagination.page === 1
              ? "text-gray-300 cursor-not-allowed"
              : "text-gray-600 hover:bg-gray-100"
              }`}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          {Array.from({ length: Math.min(pagination.pages, 3) }, (_, i) => {
            let pageNum;
            if (pagination.pages <= 3) {
              pageNum = i + 1;
            } else if (pagination.page === 1) {
              pageNum = i + 1;
            } else if (pagination.page === pagination.pages) {
              pageNum = pagination.pages - 2 + i;
            } else {
              pageNum = pagination.page - 1 + i;
            }
            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`px-3 py-1 border rounded-md ${pagination.page === pageNum
                  ? "bg-blue-500 text-white"
                  : "text-gray-600 hover:bg-gray-100"
                  }`}
              >
                {pageNum}
              </button>
            );
          })}
          <button
            disabled={
              pagination.page === pagination.pages || pagination.pages === 0
            }
            onClick={() => handlePageChange(pagination.page + 1)}
            className={`px-3 py-1 border rounded-md ${pagination.page === pagination.pages || pagination.pages === 0
              ? "text-gray-300 cursor-not-allowed"
              : "text-gray-600 hover:bg-gray-100"
              }`}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
